import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Grid.scss';
import { DataCollectionFieldModel } from '../../pages/Assessment/types';
import { fieldsConfig } from '../common/TabContent';
import { SelectInput } from '../Form/Select';
import { useDispatch, useSelector } from 'react-redux';
import {
  addGridRowAction,
  removeGridRowAction,
} from '../../store/assessmentSingle/actions';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import InfoIcon from '../../../src/assets/icons/ic-info.svg';
import { translateTabContentText } from 'utils/helpers/useTranslation';
import { useTooltipWithScrollClose } from '../../hooks/useTooltipWithScrollClose';
import { CONDITIONS } from 'utils/helpers/enum';
import { checkAllConditionsFollow } from 'utils/helpers/useCondition';
import { TableHeader } from './TableHeaderWithImg';
import { IAppState } from 'store';
import { IAssessmentSingleState } from 'store/assessmentSingle/types';
import { ImageInHeader } from './Image';
import { showField } from 'utils/helpers/showField';
import { getMirroredFieldValue } from 'utils/helpers/mirrored.functions';
import MultiSelect from 'components/MultiSelect';
import { getDisplayRowsNumber } from 'utils/helpers/grid.helpers';
import {
  getDisplayUnitPostfix,
  IUnitsMapping,
  UnitSystemKey,
} from 'utils/helpers/units.helpers';

const checkFeedStageSelected = (fieldName: string, data: any): boolean => {

  let feedIntakeField = null;

  if (data && Array.isArray(data)) {
    for (const assessment of data) {
      if (assessment.Pages) {
        for (const page of assessment.Pages) {
          if (page.Fields) {
            for (const field of page.Fields) {
              if (field.Template.DisplayName === 'Feed Intake') {
                feedIntakeField = field;
                break;
              }
            }
          }
          if (feedIntakeField) break;
        }
      }
      if (feedIntakeField) break;
    }
  } else if (data && typeof data === 'object' && data.Fields) {
    for (const field of data.Fields) {
      if (field.Template.DisplayName === 'Feed Intake') {
        feedIntakeField = field;
        break;
      }
    }
  }

  if (!feedIntakeField) {
    return true;
  }

  if (feedIntakeField.Template.FieldType.Name === 'Grid' && feedIntakeField.GridChildren) {

    let valuesList = null;
    if (feedIntakeField.GridChildren.length > 0 && feedIntakeField.GridChildren[0].length > 0) {
      const firstField = feedIntakeField.GridChildren[0][0];
      if (firstField.Template.Parameters && firstField.Template.Parameters.ValuesList) {
        valuesList = firstField.Template.Parameters.ValuesList;
      }
    }

    const selectedStageIds = [];
    feedIntakeField.GridChildren.forEach(row => {
      row.forEach(field => {
        if (field.Template.DisplayName === 'Feed Stages' && field.Value && field.Value !== null && field.Value !== '') {
          selectedStageIds.push(field.Value);
        }
      });
    });


    const feedStageMapping: { [key: string]: string[] } = {
      'Pre-Lay 1': ['Pre-Lay 1'],
      'Pre-Lay 2': ['Pre-Lay 2'],
      'Pre-Lay 3': ['Pre-Lay 3'],
      'Early Lay 1': ['Early Lay 1'],
      'Early Lay 2': ['Early Lay 2'],
      'Early Lay 3': ['Early Lay 3'],
      'Peak Lay 1': ['Peak Lay 1'],
      'Peak Lay 2': ['Peak Lay 2'],
      'Peak Lay 3': ['Peak Lay 3'],
      'Late Lay 1': ['Late Lay 1'],
      'Late Lay 2': ['Late Lay 2'],
      'Late Lay 3': ['Late Lay 3']
    };

    const matchingStages = feedStageMapping[fieldName];
    if (!matchingStages) {
      return true;
    }

    if (valuesList && valuesList.Values) {
      for (const stageName of matchingStages) {
        const stageValue = valuesList.Values.find(v => v.Name === stageName);
        if (stageValue && selectedStageIds.includes(stageValue.ID)) {
          return true;
        }
      }
    }
  }

  return false;
};

interface IProps {
  allFields: any;
  newData?: any;
  data: DataCollectionFieldModel;
  pageNum: number;
  assId: number;
  translations: any;
  setChangedFieldsId?: React.Dispatch<React.SetStateAction<any>>;
  fieldsDisabled?: boolean;
  unitsMapping?: IUnitsMapping | null;
  selectedUnitSystemKey?: UnitSystemKey | null;
}

export const Grid = ({
  newData,
  allFields,
  data,
  pageNum,
  assId,
  translations,
  setChangedFieldsId,
  fieldsDisabled = false,
  unitsMapping,
  selectedUnitSystemKey,
}: IProps) => {
  const dispatch = useDispatch();
  const [lengthFields, setLengthFields] = useState(true);
  const [minLengthFields, setMinLengthFields] = useState(false);
  const [showRowsAsColumns, setShowRowsAsColumns] = useState(false);
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const [fixedTextItemsArray, setFixedTextItemsArray] = useState([]);
  const [showHeadingTooltip, setShowHeadingTooltip] = useTooltipWithScrollClose();
  const [
    isFieldIndexForFixedTextItemsFound,
    setIsFieldIndexForFixedTextItemsFound,
  ] = useState(false);
  const [
    fieldIndexToDisplayFixedTextItems,
    setFieldIndexToDisplayFixedTextItems,
  ] = useState(-1);
  const ScrollRef = useRef<any>();
  const [rowsCounter, setRowsCounter] = useState(
    data?.Template?.Validator?.AllRowsVisible
      ? data?.Template?.Validator?.MaxRows || 0
      : getDisplayRowsNumber(
        data?.GridChildren || [],
        data?.Template?.Validator?.MinRows || 0,
      ),
  );
  const initialMaxAmount = useMemo(() => data?.GridChildren?.length || 0, [data]);
  const onAddRowClick = () => {
    setRowsCounter(prevRowsCounter => prevRowsCounter + 1);
    dispatch(addGridRowAction({ assId, pageNum, gridId: data?.Template?.ID }));
  };

  const onRemoveRowClick = () => {
    setRowsCounter(prevRowsCounter => Math.max(prevRowsCounter - 1, 1));
    dispatch(
      removeGridRowAction({
        assId,
        pageNum,
        gridId: data?.Template?.ID,
        removeCount: rowsCounter,
      }),
    );

    setChangedFieldsId(prev => {
      if (!prev.includes(data.Guid)) {
        return [...prev, data.Guid];
      }
      return prev;
    });
  };

  useEffect(() => {
    if (initialMaxAmount > rowsCounter && rowsCounter === 1) {
      const removeCount = initialMaxAmount - rowsCounter;
      for (let i = 0; i < removeCount; i++) {
        onRemoveRowClick();
      }
    }
  }, [initialMaxAmount, rowsCounter]);

  useEffect(() => {
    if (data?.Template?.DisplayName === 'General Ingredients') {

      if (allFields && Array.isArray(allFields)) {
        for (const assessment of allFields) {
          if (assessment.Pages) {
            for (const page of assessment.Pages) {
              if (page.Fields) {
                for (const field of page.Fields) {
                  if (field.Template.DisplayName === 'Feed Intake' ||
                    field.Template.Name === 'feed_intake' ||
                    field.Template.DisplayName?.toLowerCase().includes('feed intake')) {
                  }
                }
              }
            }
          }
        }
      } else if (allFields && typeof allFields === 'object' && allFields.Fields) {
        for (const field of allFields.Fields) {
          if (field.Template.DisplayName === 'Feed Intake' ||
            field.Template.Name === 'feed_intake' ||
            field.Template.DisplayName?.toLowerCase().includes('feed intake')) {
          }
        }
      }
    }
  }, [data, allFields]);



  useMemo(() => {
    data.Template?.Parameters?.RowTemplate?.FixedTextItems
      ? setFixedTextItemsArray(
        data.Template?.Parameters?.RowTemplate?.FixedTextItems,
      )
      : setFixedTextItemsArray([]);
    data.GridChildren.length < 1
      ? setLengthFields(false)
      : setLengthFields(true);
    data.GridChildren[0]?.length > 1
      ? setMinLengthFields(true)
      : setMinLengthFields(false);
    data.Template.Parameters?.ShowRowsAsColumns
      ? setShowRowsAsColumns(true)
      : setShowRowsAsColumns(false);
  }, [data]);

  const handleChange = useCallback(() => {
    setChangedFieldsId(prev => {
      if (!prev.includes(data.Guid)) {
        return [...prev, data.Guid];
      }
      return prev;
    });
  }, [setChangedFieldsId, data.Guid]);

  useEffect(() => {
    if (fieldIndexToDisplayFixedTextItems === -1 && data.GridChildren?.[0]) {
      const index = data.GridChildren[0].findIndex(f => {
        const visible = f.Template.InitiallyVisibleOnLoad;
        const noDisplayConditions =
          !f.Template.DisplayConditions || f.Template.DisplayConditions.length < 1;

        const conditionToShow =
          visible ||
          (!visible && noDisplayConditions) ||
          (!visible && !noDisplayConditions && checkAllConditionsFollow(f, newData));

        const isShowField = showField(
          f.Template.Archived,
          f.Template.VisibleExternally,
        );

        return conditionToShow && isShowField;
      });

      if (index !== -1) {
        setFieldIndexToDisplayFixedTextItems(index);
        setIsFieldIndexForFixedTextItemsFound(true);
      }
    }
  }, [data, newData, fieldIndexToDisplayFixedTextItems]);

  useEffect(() => {
    const checkContainerWidth = () => {
      const containerWidth = window.innerWidth;
      const fieldWidth = 236;
      const fieldsCount = data.GridChildren[0]?.length || 0;
      const totalFieldsWidth = fieldsCount * fieldWidth;



      if (totalFieldsWidth > containerWidth) {
        setMinLengthFields(true);
      }
    };

    const timeoutId = setTimeout(checkContainerWidth, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [data.GridChildren, newData, minLengthFields]);

  if (!data || !data.Template) {
    return null;
  }

  return (
    <div className={`grid-wrapper ${fieldsDisabled ? 'readonly-mode' : ''}`}>
      <div className="grid-heading">
        <p className="grid-title">
          {translateTabContentText(
            data?.Template?.ImageOnly ? '' : data?.Template?.DisplayName,
            translations,
          )}
        </p>
        {data.Template.Validator?.AllRowsVisible ||
          !data.GridChildren[0]?.length ||
          !data.GridChildren.length ? (
          <></>
        ) : (
          <>
            <button
              type="button"
              onClick={onAddRowClick}
              disabled={fieldsDisabled || rowsCounter >= (data.Template.Validator?.MaxRows || 0)}
            >
              +
            </button>
            <button
              type="button"
              onClick={onRemoveRowClick}
              disabled={fieldsDisabled || rowsCounter === (data.Template.Validator?.MinRows || 0)}
            >
              -
            </button>
          </>
        )}
        {data.Template.HelpText && (
          <div className="heading-tooltip">
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="bottom"
              show={showHeadingTooltip}
              onToggle={setShowHeadingTooltip}
              overlay={
                <Tooltip id={`tooltip-${data.Template.DisplayName}`}>
                  {translateTabContentText(
                    data.Template.HelpText,
                    translations,
                  )}
                </Tooltip>
              }
            >
              <img
                src={InfoIcon}
                alt="info img"
                className="tooltip-trigger"
                data-toggle="tooltip"
              />
            </OverlayTrigger>
          </div>
        )}
      </div>
      <hr />
      <div
        className="grid-fields scroll"
        style={{
          overflowX: 'auto',
          minHeight:
            selectMenuOpen && minLengthFields && !showRowsAsColumns
              ? '300px'
              : 'auto',
          maxWidth: '100%',
          padding: '10px 0',
          whiteSpace: minLengthFields ? 'initial' : 'unset',
          verticalAlign: 'top',
          position: 'relative',
          visibility: lengthFields ? 'unset' : 'hidden',
          marginBottom: ' 10px',
        }}
      >
        <>
          <div className={showRowsAsColumns ? 'table-col' : 'grid-table'}>
            <div className="fields-row">
              <div style={{display: 'flex'}} className={showRowsAsColumns ? 'table-column' : ''}>
                {data.GridChildren[0]?.map(f => {
                  const visible = f.Template.InitiallyVisibleOnLoad;
                  const noDisplayConditions =
                    !f.Template.DisplayConditions ||
                    f.Template.DisplayConditions.length < 1;

                  const conditionToShow =
                    visible ||
                    (!visible && noDisplayConditions) ||
                    (!visible &&
                      !noDisplayConditions &&
                      checkAllConditionsFollow(f, newData));
                  const isShowField = showField(
                    f.Template.Archived,
                    f.Template.VisibleExternally,
                  );



                  return conditionToShow && isShowField ? (
                    <TableHeader
                      headerText={translateTabContentText(
                        f.Template.DisplayName,
                        translations,
                      )}
                      template={f.Template}
                      translations={translations}
                      showRowsAsColumns={showRowsAsColumns}
                    />
                  ) : (
                    <></>
                  );
                })}
              </div>
            </div>

            <>
              {data.GridChildren &&
                data.GridChildren.slice(0, rowsCounter).map(
                  (fieldsArray, index) => {
                    return (
                      <div className="fields-row " key={`${data.Guid}${index}`}>
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            className={showRowsAsColumns ? 'table-column' : ''}
                          >
                            {fieldsArray.map((f, fieldIndex) => {
                              const ifCheckbox =
                                f.Template.FieldType.Name === 'Checkbox' &&
                                  !f.Template.HasImage
                                  ? 'checkbox-fit'
                                  : '';
                              const visible = f.Template.InitiallyVisibleOnLoad;
                              const noDisplayConditions =
                                !f.Template.DisplayConditions ||
                                f.Template.DisplayConditions.length < 1;

                              const conditionToShow =
                                visible ||
                                (!visible && noDisplayConditions) ||
                                (!visible &&
                                  !noDisplayConditions &&
                                  checkAllConditionsFollow(f, newData));
                              const isShowField = showField(
                                f.Template.Archived,
                                f.Template.VisibleExternally,
                              );

                              let shouldShowField = conditionToShow && isShowField;
                              if (data.Template.DisplayName === 'General Ingredients' && f.Template.DisplayName !== 'Feed') {
                                shouldShowField = conditionToShow && isShowField && checkFeedStageSelected(f.Template.DisplayName, allFields);
                              }
                              let parentMirrorData = { name: '', options: [] };
                              if (
                                f?.Template?.FieldType?.Name === 'Mirror' &&
                                f?.Template?.Parameters
                                  ?.FieldTemplateToMirror_ID !== 0
                              ) {
                                parentMirrorData = getMirroredFieldValue(
                                  f?.Template?.Parameters
                                    ?.FieldTemplateToMirror_ID,
                                  newData,
                                  index,
                                );
                              }

                              if (fieldsConfig[f.Template.FieldType.Name]) {
                                let Component;
                                if (
                                  f.Template.FieldType.Name === 'Text' &&
                                  f.Template.Parameters?.MultiLine
                                ) {
                                  Component = fieldsConfig['Textarea'];
                                } else if (
                                  f.Template.FieldType.Name &&
                                  f.Template.FieldType.Discriminator ===
                                  'ImageFieldType'
                                ) {
                                  Component =
                                    fieldsConfig['ImageUploadComponent'];
                                } else {
                                  Component =
                                    fieldsConfig[f.Template.FieldType.Name];
                                }
                                if (f.Template.FieldType.Name === 'Selection') {
                                  return (
                                    <div
                                      className={
                                        !shouldShowField
                                          ? 'hide'
                                          : ''
                                      }
                                    >
                                      <div
                                        className={
                                          !shouldShowField
                                            ? 'hide'
                                            : ''
                                        }
                                      >
                                        {index === 0 && (
                                          <ImageInHeader
                                            template={f.Template}
                                            HasImage={f.Template.HasImage}
                                            imageID={f.Template.ID}
                                            showRowsAsColumns={
                                              showRowsAsColumns
                                            }
                                          />
                                        )}
                                        {fixedTextItemsArray.length > 0 &&
                                          fieldIndex ===
                                          fieldIndexToDisplayFixedTextItems ? (
                                          <div className="fit-fixed-text">
                                            <p className="fixed-text-item">
                                              {fixedTextItemsArray.length > 0 &&
                                                fieldIndex ===
                                                fieldIndexToDisplayFixedTextItems
                                                ? fixedTextItemsArray[index]
                                                  ?.DisplayText
                                                : ''}
                                            </p>

                                            <SelectInput
                                              isGrid={true}
                                              data={data}
                                              setOpenMenu={setSelectMenuOpen}
                                              selectMenuOpen={selectMenuOpen}
                                              setChangedFieldsId={
                                                setChangedFieldsId
                                              }
                                              translations={translations}
                                              key={f.Guid}
                                              name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                              imageFile={f.Template?.ImageFile}
                                              OriginalHeight={
                                                f.Template?.OriginalHeight
                                              }
                                              OriginalSize={
                                                f.Template?.OriginalSize
                                              }
                                              OriginalWidth={
                                                f.Template?.OriginalWidth
                                              }
                                              ImageHeight={
                                                f.Template?.ImageHeight
                                              }
                                              MaintainAspectRatio={
                                                f.Template?.MaintainAspectRatio
                                              }
                                              conditionToShow={shouldShowField}
                                              ImageWidth={
                                                f.Template?.ImageWidth
                                              }
                                              options={f.Template.Parameters?.ValuesList?.Values?.map(
                                                o => ({
                                                  value: o.ID,
                                                  label: o.Name,
                                                }),
                                              )}
                                              disabled={fieldsDisabled}
                                              required={f.Template.Required}
                                              backgroundColour={f.Template?.BackgroundColour}
                                            />
                                          </div>
                                        ) : (
                                          <SelectInput
                                            isGrid={true}
                                            data={data}
                                            setOpenMenu={setSelectMenuOpen}
                                            selectMenuOpen={selectMenuOpen}
                                            setChangedFieldsId={
                                              setChangedFieldsId
                                            }
                                            translations={translations}
                                            key={f.Guid}
                                            name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                            imageFile={f.Template?.ImageFile}
                                            OriginalHeight={
                                              f.Template?.OriginalHeight
                                            }
                                            OriginalSize={
                                              f.Template?.OriginalSize
                                            }
                                            OriginalWidth={
                                              f.Template?.OriginalWidth
                                            }
                                            ImageHeight={
                                              f.Template?.ImageHeight
                                            }
                                            MaintainAspectRatio={
                                              f.Template?.MaintainAspectRatio
                                            }
                                            conditionToShow={conditionToShow}
                                            ImageWidth={f.Template?.ImageWidth}
                                            options={f.Template.Parameters?.ValuesList?.Values?.map(
                                              o => ({
                                                value: o.ID,
                                                label: o.Name,
                                              }),
                                            )}
                                            disabled={fieldsDisabled}
                                            required={f.Template.Required}
                                            backgroundColour={f.Template?.BackgroundColour}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                if (
                                  f.Template.FieldType.Name === 'MultiSelection'
                                ) {
                                  return (
                                    <div
                                      className={
                                        !shouldShowField
                                          ? 'hide'
                                          : ''
                                      }
                                    >
                                      <div
                                        className={
                                          !shouldShowField
                                            ? 'hide'
                                            : ''
                                        }
                                      >
                                        {index === 0 && (
                                          <ImageInHeader
                                            template={f.Template}
                                            HasImage={f.Template.HasImage}
                                            imageID={f.Template.ID}
                                            showRowsAsColumns={
                                              showRowsAsColumns
                                            }
                                          />
                                        )}
                                        {fixedTextItemsArray.length > 0 &&
                                          fieldIndex ===
                                          fieldIndexToDisplayFixedTextItems ? (
                                          <div className="fit-fixed-text">
                                            <p className="fixed-text-item">
                                              {fixedTextItemsArray.length > 0 &&
                                                fieldIndex ===
                                                fieldIndexToDisplayFixedTextItems
                                                ? fixedTextItemsArray[index]
                                                  ?.DisplayText
                                                : ''}
                                            </p>
                                            <MultiSelect
                                              isGrid={true}
                                              data={data}
                                              setOpenMenu={setSelectMenuOpen}
                                              selectMenuOpen={selectMenuOpen}
                                              setChangedFieldsId={
                                                setChangedFieldsId
                                              }
                                              translations={translations}
                                              key={f.Guid}
                                              name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                              imageFile={f.Template?.ImageFile}
                                              OriginalHeight={
                                                f.Template?.OriginalHeight
                                              }
                                              OriginalSize={
                                                f.Template?.OriginalSize
                                              }
                                              OriginalWidth={
                                                f.Template?.OriginalWidth
                                              }
                                              ImageHeight={
                                                f.Template?.ImageHeight
                                              }
                                              MaintainAspectRatio={
                                                f.Template?.MaintainAspectRatio
                                              }
                                              conditionToShow={shouldShowField}
                                              ImageWidth={
                                                f.Template?.ImageWidth
                                              }
                                              options={f.Template.Parameters?.ValuesList?.Values?.map(
                                                o => ({
                                                  value: o.ID,
                                                  label: o.Name,
                                                }),
                                              )}
                                              disabled={fieldsDisabled}
                                              required={f.Template.Required}
                                              backgroundColour={f.Template?.BackgroundColour}
                                            />
                                          </div>
                                        ) : (
                                          <MultiSelect
                                            isGrid={true}
                                            data={data}
                                            setOpenMenu={setSelectMenuOpen}
                                            selectMenuOpen={selectMenuOpen}
                                            setChangedFieldsId={
                                              setChangedFieldsId
                                            }
                                            translations={translations}
                                            key={f.Guid}
                                            name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                            imageFile={f.Template?.ImageFile}
                                            OriginalHeight={
                                              f.Template?.OriginalHeight
                                            }
                                            OriginalSize={
                                              f.Template?.OriginalSize
                                            }
                                            OriginalWidth={
                                              f.Template?.OriginalWidth
                                            }
                                            ImageHeight={
                                              f.Template?.ImageHeight
                                            }
                                            MaintainAspectRatio={
                                              f.Template?.MaintainAspectRatio
                                            }
                                            conditionToShow={shouldShowField}
                                            ImageWidth={f.Template?.ImageWidth}
                                            options={f.Template.Parameters?.ValuesList?.Values?.map(
                                              o => ({
                                                value: o.ID,
                                                label: o.Name,
                                              }),
                                            )}
                                            disabled={fieldsDisabled}
                                            required={f.Template.Required}
                                            backgroundColour={f.Template?.BackgroundColour}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <>
                                    <div className={ifCheckbox}>
                                      <div>
                                        <div
                                          className={
                                            !shouldShowField
                                              ? 'hide'
                                              : ''
                                          }
                                          onChange={handleChange}
                                        >
                                          {index === 0 && (
                                            <ImageInHeader
                                              template={f.Template}
                                              HasImage={f.Template.HasImage}
                                              imageID={f.Template.ID}
                                              showRowsAsColumns={
                                                showRowsAsColumns
                                              }
                                            />
                                          )}
                                          <div className={ifCheckbox}>
                                            {fixedTextItemsArray.length > 0 &&
                                              fieldIndex ===
                                              fieldIndexToDisplayFixedTextItems ? (
                                              <div className="fit-fixed-text">
                                                <p className="fixed-text-item">
                                                  {fixedTextItemsArray.length >
                                                    0 &&
                                                    fieldIndex ===
                                                    fieldIndexToDisplayFixedTextItems
                                                    ? fixedTextItemsArray[index]
                                                      ?.DisplayText
                                                    : ''}
                                                </p>
                                                <Component
                                                  key={f.Guid}
                                                  name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                                  imageFile={
                                                    f.Template?.ImageFile
                                                  }
                                                  OriginalHeight={
                                                    f.Template?.OriginalHeight
                                                  }
                                                  gridColumnIndex={index}
                                                  data={data}
                                                  fieldID={f.ID}
                                                  OriginalSize={
                                                    f.Template?.OriginalSize
                                                  }
                                                  OriginalWidth={
                                                    f.Template?.OriginalWidth
                                                  }
                                                  parentMirrorData={
                                                    parentMirrorData
                                                  }
                                                  ImageHeight={
                                                    f.Template?.ImageHeight
                                                  }
                                                  ImageWidth={
                                                    f.Template?.ImageWidth
                                                  }
                                                  setChangedFieldsId={
                                                    setChangedFieldsId
                                                  }
                                                  fieldData={f}
                                                  isGrid={true}
                                                  newData={newData}
                                                  MaintainAspectRatio={
                                                    f.Template
                                                      ?.MaintainAspectRatio
                                                  }
                                                  template={f.Template}
                                                  toolTipText={translateTabContentText(
                                                    f.Template.HelpText,
                                                    translations,
                                                  )}
                                                  postfixText={getDisplayUnitPostfix(
                                                    f,
                                                    selectedUnitSystemKey,
                                                    unitsMapping,
                                                  )}
                                                  oneField={f}
                                                  disabled={fieldsDisabled}
                                                  required={f.Template.Required}
                                                  backgroundColour={f.Template?.BackgroundColour}
                                                />
                                              </div>
                                            ) : (
                                              <>
                                                <Component
                                                  key={f.Guid}
                                                  name={`${data.Guid}[${index}][${f.Template.Name}]`}
                                                  imageFile={
                                                    f.Template?.ImageFile
                                                  }
                                                  OriginalHeight={
                                                    f.Template?.OriginalHeight
                                                  }
                                                  gridColumnIndex={index}
                                                  data={data}
                                                  fieldID={f.ID}
                                                  OriginalSize={
                                                    f.Template?.OriginalSize
                                                  }
                                                  OriginalWidth={
                                                    f.Template?.OriginalWidth
                                                  }
                                                  parentMirrorData={
                                                    parentMirrorData
                                                  }
                                                  ImageHeight={
                                                    f.Template?.ImageHeight
                                                  }
                                                  ImageWidth={
                                                    f.Template?.ImageWidth
                                                  }
                                                  setChangedFieldsId={
                                                    setChangedFieldsId
                                                  }
                                                  fieldData={f}
                                                  isGrid={true}
                                                  newData={newData}
                                                  MaintainAspectRatio={
                                                    f.Template
                                                      ?.MaintainAspectRatio
                                                  }
                                                  template={f.Template}
                                                  toolTipText={translateTabContentText(
                                                    f.Template.HelpText,
                                                    translations,
                                                  )}
                                                  postfixText={getDisplayUnitPostfix(
                                                    f,
                                                    selectedUnitSystemKey,
                                                    unitsMapping,
                                                  )}
                                                  oneField={f}
                                                  disabled={fieldsDisabled}
                                                  required={f.Template.Required}
                                                  backgroundColour={f.Template?.BackgroundColour}
                                                />
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                );
                              }
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
            </>
          </div>
        </>
      </div>
    </div>
  );
};
